import type { TaxMasterData } from '@/shared/hooks/useDocumentMasterData';

/** A slab reads as "GST 18%" when its name doesn't already say the rate. */
export function taxSlabLabel(tax: Pick<TaxMasterData, 'name' | 'rate'>): string {
  return tax.rate > 0 && !tax.name.includes(String(tax.rate)) ? `${tax.name} (${tax.rate}%)` : tax.name;
}

interface Props {
  /** Active slabs from master data. */
  slabs: TaxMasterData[]
  value: string
  /** The slab's rate comes back with it so the caller can copy it onto the line. Undefined when the value is cleared or the slab was retired. */
  onChange: (taxMasterId: string, rate?: number) => void
  className?: string
  placeholder?: string
  /** Draws the red ring when the form needs a tax type and this line has none. */
  invalid?: boolean
}

export function TaxSlabSelect({ slabs, value, onChange, className = '', placeholder = '— Select tax —', invalid = false }: Props) {
  const idOf = (tax: TaxMasterData) => String(tax.id || tax._id)
  const picked = slabs.find((tax) => idOf(tax) === value)
  // A document can still carry a slab that has since been retired; keep it selectable instead of silently clearing it.
  const retired = Boolean(value) && !picked

  return (
    <select
      aria-label="Tax type"
      className={
        'w-full rounded-md border bg-white dark:bg-slate-800 px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 ' +
        (invalid ? 'border-rose-400 dark:border-rose-500 ' : 'border-slate-200 dark:border-slate-700 ') +
        className
      }
      value={value}
      onChange={(e) => {
        const next = slabs.find((tax) => idOf(tax) === e.target.value)
        onChange(e.target.value, next?.rate)
      }}
    >
      <option value="">{placeholder}</option>
      {retired && <option value={value}>Retired tax</option>}
      {slabs.map((tax) => (
        <option key={idOf(tax)} value={idOf(tax)}>
          {taxSlabLabel(tax)}
        </option>
      ))}
    </select>
  )
}
